import {
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDataSource,
  type CrossClientQueryDecompositionResult,
  type CrossClientQueryEvaluation,
  type CrossClientQueryEvaluationType,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
  type CrossClientQueryStartResult,
} from '../../models/CrossClient'
import { type BaseRequest } from '../models'
import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

const _TRANSCRIPTION_SERVICE_BASE_URL = import.meta.env.REACT_APP_AI_EXECUTE_API_BASE_URL!

interface CrossClientQueryDecomposeRequest extends BaseRequest {
  payload: {
    userQuestion: string
  }
}

interface CrossClientQueryStartUserQuestionPayload {
  userQuestion: string
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  sources?: CrossClientQueryDataSource[]
  clientQueryMode?: CrossClientQueryClientQueryMode
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
  predefinedQueryId?: never
}

interface CrossClientQueryStartPredefinedQueryPayload {
  predefinedQueryId: string
  sources?: CrossClientQueryDataSource[]
  clientQueryMode?: CrossClientQueryClientQueryMode
  userQuestion?: never
  decomposition?: never
  nnfDecomposition?: never
}

export type CrossClientQueryStartPayload = CrossClientQueryStartUserQuestionPayload | CrossClientQueryStartPredefinedQueryPayload

interface CrossClientQueryStartRequest extends BaseRequest {
  payload: CrossClientQueryStartPayload
}

interface CreateCrossClientQueryEvaluationRequest extends BaseRequest {
  executionId: string
  type: CrossClientQueryEvaluationType
  resultIds: string[]
}

interface CancelCrossClientQueryEvaluationRequest extends BaseRequest {
  executionId: string
  evaluationId: string
}

export const crossClientExecutionApi = createApi({
  reducerPath: 'crossClientExecutionApi',
  baseQuery: baseQueryWithTokenRefresh(_TRANSCRIPTION_SERVICE_BASE_URL),
  endpoints: (builder) => ({
    decomposeCrossClientQuery: builder.mutation<CrossClientQueryDecompositionResult, CrossClientQueryDecomposeRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/cross-client-query/decompose`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: CrossClientQueryDecompositionResult }) => response.data,
    }),
    startCrossClientQuery: builder.mutation<CrossClientQueryStartResult, CrossClientQueryStartRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/cross-client-query/start`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: CrossClientQueryStartResult }) => response.data,
    }),
    createCrossClientQueryEvaluation: builder.mutation<CrossClientQueryEvaluation, CreateCrossClientQueryEvaluationRequest>({
      query: ({ accountId, executionId, type, resultIds }) => ({
        url: `/accounts/${accountId}/cross-client-query/${executionId}/evaluations`,
        method: 'POST',
        body: { type, resultIds },
      }),
      transformResponse: (response: { data: CrossClientQueryEvaluation }) => response.data,
    }),
    cancelCrossClientQueryEvaluation: builder.mutation<void, CancelCrossClientQueryEvaluationRequest>({
      query: ({ accountId, executionId, evaluationId }) => ({
        url: `/accounts/${accountId}/cross-client-query/${executionId}/evaluations/${evaluationId}/cancel`,
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useDecomposeCrossClientQueryMutation,
  useStartCrossClientQueryMutation,
  useCreateCrossClientQueryEvaluationMutation,
  useCancelCrossClientQueryEvaluationMutation,
} = crossClientExecutionApi
